(ns dashboard.components.notes-modal
  (:require [clojure.string :refer [trim]]
            [dashboard.common :refer [format-date-time]]
            [dashboard.entity.questionnaire.db :as questionnaire.db]
            [dashboard.services.question-note :as question-note]
            [dashboard.ui :as ui]
            [nextjournal.markdown :as md]
            [nextjournal.markdown.transform :as md.transform]
            [re-frame.core :as rf]
            [reagent.core :as r]))

(rf/reg-sub
 ::user-email
 (fn [db [_]]
   (get-in db [:user :user-email])))

(rf/reg-sub
 ::attachment-name
 (fn [db [_ attachment-id]]
   (get-in db [:page :questionnaire-detail :details :attachments attachment-id :filename])))

(defn event-content [data]
  (let [{:keys [event-type status attachment _answer response additional-information]} data
        attachment-name @(rf/subscribe [::attachment-name (str "ATT__" attachment)])
        status-label (get-in questionnaire.db/status->question-status-options [status :label] "Unknown")]
    (case event-type
      "answer-changed" [:span.is-size-10.has-text-grey (str "Selected an answer")] ; TODO: Show answer content when we can easily get an answer by id
      "answer-edited" [:span.is-size-10.has-text-grey "Edited an answer: " [:b response] ", \"" [:i additional-information] "\""]
      "status-changed" [:span.is-size-10.has-text-grey (str "Changed question status to \"" status-label "\"")]
      "attachment-added" [:span.is-size-10.has-text-grey "Attached document " [:b attachment-name]]
      "attachment-removed" [:span.is-size-10.has-text-grey "Removed document " [:b attachment-name]])))

(defn note-item [email {:keys [type author content created question data]}]
  (let [last-read @(rf/subscribe [::question-note/question-note-last-read-time question])
        is-ours   (= email author)
        is-unread (and (not is-ours)
                       (> created last-read))]
    [:div.column {:class (if is-ours "ml-6" "mr-6")}
     [:span.is-size-7
      [:b (if is-ours "You" author)]
      " on " (format-date-time created)]
     (case type
       "message" [:article.message {:class (cond
                                             is-unread "is-warning"
                                             is-ours "is-dark")}
                  [:div.message-body.p-2
                   (->> content
                        md/parse
                        (md.transform/->hiccup (assoc md.transform/default-hiccup-renderers
                                                      :table (partial md.transform/into-markup [:table.table.is-striped.is-bordered]))))
                   ]]
       "event" [:div [event-content data]])]))

(defn note->note-item [last-read-time is-last-note-read email note]
  (if (and (not is-last-note-read)
           (= (:created note) last-read-time))
    ^{:key (str "note-unread-container-" (:created note))}
    [:div
     [note-item email note]
     [:div.column.is-align-items-center
      [:hr.mb-0]
      [:center [:span.is-size-10.has-text-info  "Unread messages"]]]]
    ^{:key (str "note-" (:created note))} [note-item email note]))

(defn notes-list [{:keys [questionnaire-id question-id ref]}]
  (let [last-read         @(rf/subscribe [::question-note/question-note-last-read-time questionnaire-id question-id])
        email             @(rf/subscribe [::user-email])
        notes             @(rf/subscribe [::question-note/question-notes questionnaire-id question-id])
        unread-count      @(rf/subscribe [::question-note/question-unread-note-count questionnaire-id question-id])
        mark-as-read      (fn [_]
                            (rf/dispatch [::question-note/mark-notes-last-seen questionnaire-id question-id (-> notes last :created)]))
        is-last-note-read (or (= email (-> notes last :author))
                              (= (-> notes last :created) last-read))]
    [:<>
     (when (empty? notes)
       [:span.m-2 "No messages"])
     [:div.column {:ref   ref
                   :style {:max-height "50vh" :overflow "scroll"}}
      (map (partial note->note-item last-read is-last-note-read email) notes)
      (when (> unread-count 0)
        [:center
         [:div.button.is-small.is-success.mx-auto {:on-click mark-as-read}
          [:span.icon.mr-1 [:i.fas.fa-check]]
          "Mark messages as read"]])]]))

(defn add-note-form []
  (let [content (r/atom "")]
    (fn [{:keys [questionnaire-id question-id on-send]}]
      (let [status @(rf/subscribe [::question-note/question-note-status])
            note {:question question-id
                  :content @content}
            disable-send-button? (or (empty? (trim @content))
                                     (:sending status))]
        [:form.p-2 {:style {:width "100%"}
                    :on-submit (fn [e]
                                 (.preventDefault e)

                                 (rf/dispatch [::question-note/add-note questionnaire-id question-id note])
                                 (on-send (update note :content trim))
                                 (reset! content ""))}
         [:div.columns
          [:div.column
           [:textarea.textarea {:placeholder "Your comment"
                                :rows 1
                                :value (:content note)
                                :on-change #(reset! content (.. % -target -value))}]]
          [:div.column.is-narrow
           [:input.button {:name     "submit"
                           :type     :submit
                           :value    "Send"
                           :class    [(if disable-send-button? nil "is-primary") "is-rounded"]
                           :disabled disable-send-button?}]]]]))))

(defn notes-modal [{{:keys [question-id questionnaire-id]} :ctx}]
  (let [list-ref (r/atom nil)
        on-send #(.scrollTo @list-ref 0 (.-scrollHeight @list-ref))]
    [:div.card
     [:header.card-header.has-background-secondary.is-shadowless
      [:title.card-header-title "Notes"]
      [:button.card-header-icon {:on-click #(rf/dispatch [::ui/pop-modal])}
       [:span.icon [:i.fas.fa-xmark]]]]
     [:div.card-content.p-1
      [notes-list {:ref #(do
                           (when %
                             (.scrollTo % 0 (.-scrollHeight %)))
                           (reset! list-ref %))
                   :questionnaire-id questionnaire-id
                   :question-id question-id}]]
     [:footer.card-footer
      [add-note-form {:questionnaire-id questionnaire-id
                      :question-id question-id
                      :on-send on-send}]]]))
